"use client";

import type { SupSingleVaultV3, SupWeb3PortfolioVault, SupWeb3VaultBalance } from "@/types";
import React, { useMemo } from "react";

import SupUiLoading from "@/components/Ui/Loading";
import UiTooltip from "@/components/Ui/Tooltip";
import GridCellToken from "@/components/Grid/Cell/Token";
import CartButton from "@/components/Adapter/CartButton";

import { type Column, SupDataGrid, SupText } from "@repo/core-ui";
import { twMerge } from "tailwind-merge";
import * as dn from "dnum";

import { filterExponential, formatCurrencyCompact, UiLabelChain } from "@/utils";
import config from "@/../config/dapp.config";
import useTokenSuperpositionsBalances from "@/hooks/api/useTokenSuperpositionsBalances";

interface PortfolioPositionsProps {
  singleVault: SupSingleVaultV3;
  className?: string;
}

export default function PortfolioPositions({ singleVault, className }: PortfolioPositionsProps) {
  const { data, isLoading } = useTokenSuperpositionsBalances();

  const vaultPositions = useMemo(() => {
    if (!data) {
      return [] as SupWeb3PortfolioVault[];
    }

    return data.superPositions
      .filter(singlePosition => singlePosition.vault.id === singleVault.id)
      .map(singlePosition => {
        let balance = 0n;
        balance = balance + BigInt(`${singlePosition.superposition_balance}`);
        // noinspection PointlessArithmeticExpressionJS
        const usdValue = parseFloat(filterExponential(`${singlePosition.superposition_usd_value}`));
        const percentage = data.portfolioValue == 0 ? 0 : (usdValue / data.portfolioValue) * 100;
        return {
          ...singleVault,
          chain_id: singlePosition.chain_id,
          percentage: `${percentage.toFixed(2)}%`,
          amount: balance,
          usdValue: usdValue.toFixed(2),
          hasDetails: false,
          vaultBalances: [singlePosition] as SupWeb3VaultBalance[],
        } as SupWeb3PortfolioVault;
      });
  }, [data, singleVault]);

  const columns: Column<SupWeb3PortfolioVault>[] = [
    {
      resizable: false,
      field: "amount",
      type: "number",
      align: "left",
      headerName: "Position",
      width: "30%",
      renderCell: row => (
        <div className="inline-flex whitespace-nowrap">
          {dn.format([row.amount as bigint, row.decimals], { locale: "en-US", digits: config.decimalsBigRoundingLimit })}&nbsp;{row.symbol}
        </div>
      ),
    },
    {
      field: "chain",
      headerName: "Minted on",
      align: "right",
      width: 160,
      renderCell: row => (
        <UiTooltip content="Chain where your SuperPosition was minted on">
          <div className="flex w-full cursor-help items-center justify-end -space-x-[5px]">{UiLabelChain(row.chain_id as number, true, true, false, "border-none img-bigger")}</div>
        </UiTooltip>
      ),
    },
    {
      field: "token",
      headerName: "Token",
      minWidth: 100,
      valueGetter: row => row.assets[0].symbol,
      renderCell: row => <GridCellToken singleVault={row} />,
    },
    {
      field: "usdValue",
      type: "number",
      headerName: "USD Value",
      width: 130,
      renderCell: row => formatCurrencyCompact(parseFloat(`${row.usdValue}`), config.decimalsRoundingLimit, 0, "currency", "standard"),
    },
    {
      field: "percentage",
      type: "number",
      headerName: "Portfolio %",
      width: 150,
      minWidth: 130,
    },
    {
      field: "actions",
      align: "right",
      width: 180,
      sortable: false,
      resizable: false,
      draggable: false,
      headerCellClassName: "rdg-cell-frozen sup-grid-right-sticky",
      cellClassName: "rdg-cell-frozen sup-grid-right-sticky",
      headerName: "",
      renderCell: row => <CartButton singleVault={row} vaultBalance={row.vaultBalances[0]} />,
    },
  ];

  return isLoading || vaultPositions.length > 0 ? (
    <div className={twMerge("mx-6 mt-12 min-h-[50px] font-mono", className)}>
      <SupText family="mono" variant="subtitle1" className="mb-6">
        My positions
      </SupText>
      {isLoading ? (
        <SupUiLoading className="h-[50px] w-[100px] p-[10px]" />
      ) : (
        <SupDataGrid
          className="mb-6 h-fit p-0"
          defaultSorting={[{ field: "usdValue", direction: "DESC" }]}
          data={vaultPositions}
          rowId={row => `${row.chain_id}_position_${row.id}`}
          columns={columns}
        />
      )}
    </div>
  ) : null;
}
